import { ItemsPerPage } from '@billy/design-system'
import qs from 'qs'

import { SortDirection } from '../../enums/sortDirection'
import { Metable } from '../../types/metable'
import apiZervant from '../../utils/apiZervant'
import { RecurringInvoicesSortProperty } from './enums/recurringInvoicesSortProperty'
import { RecurringInvoiceStatus } from './enums/recurringInvoiceStatus'
import { RecurringInvoiceStatusEventType } from './enums/recurringInvoiceStatusEventType'
import { RecurringInvoice } from './types/recurringInvoice'
import { RecurringInvoiceDraft } from './types/recurringInvoiceDraft'
import { RecurringInvoiceDraftPayload } from './types/recurringInvoiceDraftPayload'

interface FetchRecurringInvoicesOptions {
  page?: number
  pageSize?: ItemsPerPage
  sortDirection?: SortDirection
  sortProperty?: RecurringInvoicesSortProperty
  status?: RecurringInvoiceStatus
}

interface FetchRecurringInvoicesResponseData extends Metable {
  recurringProfiles: RecurringInvoice[]
}

export const fetchRecurringInvoices = ({
  page,
  pageSize,
  sortDirection,
  sortProperty,
  status,
}: FetchRecurringInvoicesOptions) => {
  const queryParams = qs.stringify({
    ...(page ? { page } : {}),
    ...(pageSize ? { pageSize } : {}),
    ...(sortDirection ? { sortDirection } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(status && status !== RecurringInvoiceStatus.All ? { status } : {}),
  })

  return apiZervant.restInvoice.get<FetchRecurringInvoicesResponseData>(`/v1/recurring-profiles/all?${queryParams}`)
}

interface CreateRecurringInvoiceDraftOptions {
  payload: RecurringInvoiceDraftPayload
}

type CreateRecurringInvoiceDraftResponseData = RecurringInvoiceDraft

export const createRecurringInvoiceDraft = ({ payload }: CreateRecurringInvoiceDraftOptions) => {
  return apiZervant.restInvoice.post<CreateRecurringInvoiceDraftResponseData>('/recurring-profile', payload)
}

interface UpdateRecurringInvoiceDraftOptions {
  payload: RecurringInvoiceDraftPayload
}

type UpdateRecurringInvoiceDraftResponseData = RecurringInvoiceDraft

export const updateRecurringInvoiceDraft = ({ payload }: UpdateRecurringInvoiceDraftOptions) => {
  const { id } = payload
  return apiZervant.restInvoice.put<UpdateRecurringInvoiceDraftResponseData>(`/recurring-profile/${id}`, payload)
}

export interface UpdateRecurringInvoiceStatusOptions {
  recurringInvoiceId: string
  recurringInvoiceStatus: RecurringInvoiceStatusEventType
}

type UpdateRecurringInvoiceStatusResponseData = RecurringInvoiceDraft

export const updateRecurringInvoiceStatus = ({
  recurringInvoiceId,
  recurringInvoiceStatus,
}: UpdateRecurringInvoiceStatusOptions) => {
  return apiZervant.restInvoice.post<UpdateRecurringInvoiceStatusResponseData>(
    `/recurring-profile/${recurringInvoiceId}/event/${recurringInvoiceStatus}`,
  )
}

type FetchRecurringInvoicesSummaryResponseData = {
  [key in RecurringInvoiceStatus]?: number
}

export const fetchRecurringInvoicesSummary = () =>
  apiZervant.restInvoice.get<FetchRecurringInvoicesSummaryResponseData>('/v1/recurring-profiles/status-summary')

type FetchRecurringInvoiceResponseData = RecurringInvoice

export const fetchRecurringInvoice = (recurringInvoiceId: string) => {
  return apiZervant.restInvoice.get<FetchRecurringInvoiceResponseData>(`/v1/recurring-profiles/${recurringInvoiceId}`)
}

type FetchRecurringInvoiceDraftFormatResponseData = RecurringInvoiceDraft

// this is an older Zervant endpoint and we use it now to get the draft structure
// that can be used
// - with the /html/live endpoint to get the preview
// - to load draft data to the editor
export const fetchRecurringInvoiceDraftFormat = (recurringInvoiceId: string) => {
  return apiZervant.restInvoice.get<FetchRecurringInvoiceDraftFormatResponseData>(
    `/recurring-profile/${recurringInvoiceId}`,
  )
}
